import { addScrollEndEventListener } from "../../user-agent-fallbacks/scrollend-event/scrollend-event";

/**
 * Class for registering and handling a result indicator that allows for navigation in results
 */
export class ResultIndicator {
  private headerOffset = 0;

  private previousMatch: HTMLElement | null = null;

  private currentMatch: HTMLElement | null = null;

  private nextMatch: HTMLElement | null = null;

  private matchElements: HTMLElement[] = [];

  private isScrolling: boolean = false;

  private timeoutRunning: boolean = false;

  private SCROLL_TOLERANCE = 20;

  constructor(
    private readonly indicatorOverlay: HTMLElement | null,
    private readonly previousButton: HTMLButtonElement | null,
    private readonly nextButton: HTMLButtonElement | null,
    private readonly positionIndicator: HTMLElement | null,
    private readonly header: HTMLElement | null
  ) {
    this.headerOffset = header ? header.getBoundingClientRect().height : 0;

    this.registerEventHandlers();
  }

  private registerEventHandlers() {
    document.addEventListener("scroll", () => {
      this.isScrolling = true;
    });

    addScrollEndEventListener(document, () => {
      // ensure that only one timeout is running at a time
      if (this.timeoutRunning) {
        return;
      }

      this.timeoutRunning = true;

      // event throttling
      window.setTimeout(() => {
        this.timeoutRunning = false;
        this.isScrolling = false;
        this.update();
      }, 100);
    });

    this.previousButton?.addEventListener("click", () => {
      if (this.isScrolling) {
        return;
      }
      this.scrollToPrevious();
    });

    this.nextButton?.addEventListener("click", () => {
      if (this.isScrolling) {
        return;
      }
      this.scrollToNext();
    });
  }

  private getDistanceFromTop(element: HTMLElement) {
    return element.getBoundingClientRect().top - this.headerOffset;
  }

  private scrollToElement(element: HTMLElement) {
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - this.headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }

  scrollToPrevious() {
    if (!this.previousMatch || this.matchElements.length === 0) {
      return;
    }

    this.scrollToElement(this.previousMatch);
  }

  scrollToNext() {
    if (!this.nextMatch || this.matchElements.length === 0) {
      return;
    }

    this.scrollToElement(this.nextMatch);
  }

  /**
   * Finds the previous, current and next matches
   * @private
   */
  private updateMatchedElements() {
    if (this.matchElements.length === 0) {
      return;
    }

    let previousMatch: HTMLElement | null = null;
    let currentMatch: HTMLElement | null = null;
    let nextMatch: HTMLElement | null = null;

    for (const element of this.matchElements) {
      const distance = this.getDistanceFromTop(element);
      const offset = Math.abs(distance) - this.SCROLL_TOLERANCE;

      // set previous match
      if (offset > 0 && distance < 0) previousMatch = element;

      // set current match
      if (currentMatch === null) {
        if (offset <= 0) currentMatch = element;
      }

      // set next match
      if (nextMatch === null) {
        if (offset > 0 && distance > 0) nextMatch = element;
      }
    }

    this.previousMatch = previousMatch;
    this.currentMatch = currentMatch;
    this.nextMatch = nextMatch;

    if (this.currentMatch) {
      this.isScrolling = false;
    }

    this.nextMatch =
      this.matchElements.find((element) => {
        const distance = this.getDistanceFromTop(element);
        return Math.abs(distance) - this.SCROLL_TOLERANCE > 0 && distance > 0;
      }) ?? null;
  }

  private getIndexOfMatch(match: HTMLElement | null) {
    return !match ? 0 : this.matchElements.indexOf(match);
  }

  /**
   * Updates the result position indicator
   * @private
   */
  private updateIndicator() {
    if (this.positionIndicator === null) {
      return;
    }

    let nearestIndex = 0;

    if (this.currentMatch) {
      nearestIndex = this.getIndexOfMatch(this.currentMatch);
    } else if (this.previousMatch) {
      nearestIndex = this.getIndexOfMatch(this.previousMatch);
    } else if (this.nextMatch) {
      nearestIndex = this.getIndexOfMatch(this.nextMatch);
    }

    const totalElements = this.matchElements.length;

    this.positionIndicator.innerText = `${nearestIndex + 1} / ${totalElements}`;
  }

  /**
   * Sets the state (enabled / disabled) of the result scroll buttons
   * @private
   */
  private updateButtons() {
    if (this.previousButton) {
      this.previousButton.disabled = this.previousMatch === null;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.nextMatch === null;
    }
  }

  hideOverlay() {
    if (this.indicatorOverlay === null) {
      return;
    }

    this.indicatorOverlay.hidden = true;
  }

  showOverlay() {
    if (this.indicatorOverlay === null) {
      return;
    }

    this.indicatorOverlay.hidden = false;
  }

  setResults(matchElements: HTMLElement[]) {
    this.matchElements = matchElements;

    this.update();
  }

  update() {
    if (this.matchElements.length > 0) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }

    this.headerOffset = this.header
      ? this.header.getBoundingClientRect().height
      : 0;

    this.updateMatchedElements();
    this.updateIndicator();
    this.updateButtons();
  }
}
